import React from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { SvgIconProps, Tooltip } from '@mui/material';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';

import type { StepIdentifier, StatusActivityStatus } from '@shared/patient-intake-status';

import { getStatusColour } from '../utils';
import { getStepConfig } from '../getStepConfig';

const throwNeverStatus = (status: never) => {
  throw new Error(`Status does not exist: ${status}`);
};

interface IconWrapperProps extends React.ComponentProps<typeof Tooltip> {
  children: React.ReactElement<SvgIconProps>;
}

const IconWrapper = ({ title, children, ...props }: IconWrapperProps) => (
  <Tooltip title={title} placement="top" {...props}>
    {children}
  </Tooltip>
);

type StatusIconProps = {
  status: StatusActivityStatus;
  stepIdentifier: StepIdentifier;
} & Omit<SvgIconProps, 'color'>;

export const StatusIcon = ({ status, stepIdentifier, ...iconProps }: StatusIconProps) => {
  if (!status) {
    return null;
  }

  const { colour } = getStatusColour(status);

  const stepConfig = getStepConfig(stepIdentifier);
  const stepStatusConfig = stepConfig.statuses[status];

  if (!stepStatusConfig) {
    throw new Error(`Status does not exist: ${status}`);
  }

  const { label } = stepStatusConfig;

  switch (status) {
    case 'COMPLETED':
      return (
        <IconWrapper title={label}>
          <CheckCircleIcon color={colour} className="shake" {...iconProps} />
        </IconWrapper>
      );
    case 'PENDING':
      return (
        <IconWrapper title={label}>
          <HourglassBottomRoundedIcon color={colour} className="spin" {...iconProps} />
        </IconWrapper>
      );
    case 'ERROR':
      return (
        <IconWrapper title={label}>
          <ErrorIcon color={colour} className="emphasise" {...iconProps} />
        </IconWrapper>
      );
    case 'NOT_STARTED':
      return (
        <IconWrapper title={label}>
          <PendingIcon sx={{ color: 'rgba(0,0,0,0.5)' }} className="jitter" {...iconProps} />
        </IconWrapper>
      );
    default:
      return throwNeverStatus(status);
  }
};
